import { getExtFromMime } from '@/utils/getExtFromMime';

/** Usada quando nem a URL nem o MIME do blob revelam o formato. */
const FALLBACK_EXT = 'ogg';

/**
 * Extensão real do arquivo. A chave do objeto no bucket é a fonte mais confiável
 * (o gravador decide entre webm/m4a/ogg em runtime — ver `resolveAudioPath`);
 * o MIME do blob só entra quando a URL não carrega extensão.
 */
function resolveExt(url: string, mime?: string): string {
    let pathname = url;
    try {
        pathname = new URL(url, window.location.href).pathname;
    } catch { /* URL relativa exótica: cai no valor bruto */ }

    const match = /\.([a-z0-9]{2,5})$/i.exec(pathname);
    if (match) return match[1].toLowerCase();

    return getExtFromMime(mime) ?? FALLBACK_EXT;
}

/**
 * Baixa o áudio via blob para forçar o download sem navegar/abrir aba.
 * O atributo `download` em <a> é ignorado quando a URL é cross-origin,
 * fazendo o navegador navegar para a URL e substituir a página atual.
 *
 * `cache: 'no-store'` é obrigatório, não é otimização: o <audio> do player
 * busca a mesma URL em modo no-cors e o Chrome guarda essa resposta opaca no
 * HTTP cache sem `Vary: Origin`. Um fetch CORS posterior reaproveitaria a
 * entrada e falharia com "No 'Access-Control-Allow-Origin' header is present"
 * sem sequer ir à rede — ainda que o bucket responda o header corretamente.
 *
 * @param url      URL do áudio.
 * @param baseName Nome do arquivo sem extensão; a extensão é resolvida aqui.
 */
export async function downloadAudioFile(url: string, baseName: string): Promise<void> {
    const response = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!response.ok) throw new Error('Falha na rede ao baixar áudio');

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${baseName}.${resolveExt(url, blob.type)}`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    // Revogar de imediato pode cortar o download em lote no Chrome, que lê a
    // blob URL de forma assíncrona depois do click.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
}
